import React, { useState, useEffect } from "react";
import { CircularProgress } from "@mui/material";
import { DocumentChartBarIcon } from "@heroicons/react/24/outline";
import { Breadcrumb } from "../../../components/Breadcrumb";
import { ApplicationItem } from "../../../components/ApplicationItem";
import { RoleType } from "../../../components/CreateButton/constants";
import httpClient, { TimeoutError } from "../../../utils/httpClient";
import { useData } from "../../../providers/DataProvider";
import { StatusFilterState } from "./types";
import { useAdminApplications } from "../../../hooks/useBonusQueries";
import { getPreviousQuarter } from "../../../utils/dateUtils";
import { BonusApplication } from "../../../services/bonus/types";

const STORAGE_KEY = "adminStatusOverviewFilters";

const StatusOverview: React.FC = () => {
  const { role } = useData();
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [isExporting, setIsExporting] = useState(false);

  const prevQuarter = getPreviousQuarter();

  const getInitialFilters = (): StatusFilterState => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        const yearOk = parsed.year === "All" || parsed.year < prevQuarter.year ||
          (parsed.year === prevQuarter.year && (parsed.quarter === "All" || parsed.quarter <= prevQuarter.quarter));
        if (yearOk) {
          const { applicant: legacyApplicant, ...rest } = parsed;
          return {
            ...rest,
            bonusRecipient: parsed.bonusRecipient ?? legacyApplicant ?? "",
          } as StatusFilterState;
        }
      } catch (e) {
        console.error("Failed to parse stored filters:", e);
      }
    }
    return {
      applicationType: "All",
      status: "All",
      bonusRecipient: "",
      region: "All",
      resultType: "json",
      year: prevQuarter.year,
      quarter: prevQuarter.quarter,
      page: 1,
      pageSize: 25,
    };
  };

  const [filters, setFilters] = useState<StatusFilterState>(getInitialFilters());

  // Save filters to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  }, [filters]);

  const { data: apiResponse, isLoading, error } = useAdminApplications(filters);

  const applications = apiResponse?.data || [];
  const totalCount = apiResponse?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / filters.pageSize);

  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const handleClearFilters = () => {
    const defaultFilters: StatusFilterState = {
      applicationType: "All",
      status: "All",
      bonusRecipient: "",
      region: "All",
      resultType: "json",
      year: prevQuarter.year,
      quarter: prevQuarter.quarter,
      page: 1,
      pageSize: 25,
    };
    setFilters(defaultFilters);
    localStorage.removeItem(STORAGE_KEY);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters(prev => ({ ...prev, page: 1 }));
  };

  const breadcrumbPages = [
    {
      name: "Admin",
      href: "#",
      current: false,
    },
    {
      name: "Dashboard",
      href: "#",
      current: false,
    },
    {
      name: "Status Overview",
      href: "/admin/dashboard/status-overview",
      current: true,
      icon: (props: React.ComponentProps<"svg">) => (
        <DocumentChartBarIcon
          {...props}
          className="w-6 h-6 text-primary-500 transition duration-75 group-hover:text-gray-900"
        />
      ),
    },
  ];

  const handleFilterChange = (field: keyof StatusFilterState, value: string | number) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      page: 1,
    }));
  };

  const formatDateTime = (): string => {
    const now = new Date();
    const centralTime = now.toLocaleString('sv-SE', {
      timeZone: 'America/Chicago',
      hour12: false
    });
    return centralTime.replace(/[-: ]/g, '').replace(/(\d{8})(\d{6})/, '$1_$2');
  };

  const handleExportToExcel = async () => {
    setIsExporting(true);

    const formattedDate = formatDateTime();
    const filename = `QBA_Status_${filters.year}_${filters.quarter}_${formattedDate}.xlsx`;

    try {
      const response = await httpClient.get(
        "/api/manage/applications",
        {
          params: {
            year: filters.year,
            quarter: filters.quarter,
            applicationType: filters.applicationType,
            status: filters.status,
            BonusRecipient: filters.bonusRecipient,
            region: filters.region,
            resultType: "excel",
          },
          responseType: 'blob',
          headers: {
            roleType: role
          }
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: unknown) {
      if (error instanceof TimeoutError) {
        console.error(`Request timed out for ${(error as TimeoutError).config.method.toUpperCase()} ${(error as TimeoutError).config.url} — ${(error as TimeoutError).message}. Please check if the export went through before trying again`);
      } else {
        console.error("Error exporting to Excel:", error);
      }
    } finally {
      setIsExporting(false);
    }
  };

  const toggleRowExpansion = (id: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Generate year options (current year and 2 years back)
  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2];

  // Determine max allowed quarter for selected year
  const getMaxQuarter = (year: number): number => {
    if (year < prevQuarter.year) return 4;
    if (year === prevQuarter.year) return prevQuarter.quarter;
    return 1; // Future year, should not happen
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="xl:pl-0 flex-grow flex flex-col">
        <main className="flex-grow flex flex-col overflow-hidden">
          <div className="relative isolate overflow-hidden pt-6 flex-grow flex flex-col">
            <div className="mx-auto w-full px-4 sm:px-6 lg:px-8">
              <div className="mb-6">
                <Breadcrumb pages={breadcrumbPages} />
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Search Filters
                </h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Year */}
                    <div>
                      <label
                        htmlFor="year"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Year
                      </label>
                      <select
                        id="year"
                        value={filters.year}
                        onChange={(e) => {
                          const val = e.target.value;
                          const newYear = val === "All" ? "All" : parseInt(val);
                          // If "All Years", also clear quarter
                          if (newYear === "All") {
                            setFilters(prev => ({ ...prev, year: "All", quarter: "All", page: 1 }));
                          } else {
                            const maxQ = getMaxQuarter(newYear as number);
                            const newQuarter =
                              filters.quarter === "All" || (filters.quarter as number) > maxQ
                                ? maxQ
                                : filters.quarter;
                            setFilters(prev => ({ ...prev, year: newYear, quarter: newQuarter, page: 1 }));
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="All">All Years</option>
                        {yearOptions.map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>

                    </div>

                    {/* Quarter */}
                    <div>
                      <label
                        htmlFor="quarter"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Quarter
                      </label>
                      <select
                        id="quarter"
                        value={filters.quarter}
                        disabled={filters.year === "All"}   // forces "All" quarters when year is "All"
                        onChange={(e) => {
                          const val = e.target.value;
                          handleFilterChange("quarter", val === "All" ? "All" : parseInt(val));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      >
                        <option value="All">All Quarters</option>
                        {[1, 2, 3, 4].map(q => (
                          <option
                            key={q}
                            value={q}
                            disabled={filters.year !== "All" && q > getMaxQuarter(filters.year as number)}
                          >
                            Q{q}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Application Type */}
                    <div>
                      <label
                        htmlFor="applicationType"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Application Type
                      </label>
                      <select
                        id="applicationType"
                        value={filters.applicationType}
                        onChange={(e) =>
                          handleFilterChange("applicationType", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="All">All</option>
                        <optgroup label="Customer Care">
                          <option value="AllCustomerCare">All Customer Care</option>
                          <option value="CCM1">CCM1</option>
                          <option value="CCM2">CCM2</option>
                          <option value="CCM3">CCM3</option>
                          <option value="DCCM">DCCM</option>
                        </optgroup>
                        <optgroup label="Construction">
                          <option value="AllConstruction">All Construction</option>
                          <option value="CM3">CM3</option>
                          <option value="PM">PM</option>
                          <option value="SPM">SPM</option>
                        </optgroup>
                      </select>
                    </div>

                    {/* Status */}
                    <div>
                      <label
                        htmlFor="status"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Status
                      </label>
                      <select
                        id="status"
                        value={filters.status}
                        onChange={(e) => handleFilterChange("status", e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="All">All</option>

                        <optgroup label="Applicant">
                          <option value="All-Applicant">All Applicant</option>
                          <option value="Draft">Draft</option>
                          <option value="Rejected">Rejected</option>
                          <option value="Cancelled">Cancelled</option>
                        </optgroup>

                        <optgroup label="Customer Care Review">
                          <option value="Customer-Care Review">All - Customer Care Review</option>
                          <option value="DCCM-Review">DCCM Review</option>
                          <option value="VP-Review">VP Review (CC)</option>
                        </optgroup>

                        <optgroup label="Construction Review">
                          <option value="Construction Review">All - Construction Review</option>
                          <option value="SPM-Review">SPM Review</option>
                          <option value="DM-Review">DM Review</option>
                          <option value="VP-Review">VP Review (Const)</option>
                        </optgroup>

                        <optgroup label="Operations">
                          <option value="Ops">Ops Review</option>
                        </optgroup>

                        <optgroup label="Payroll">
                          <option value="Payroll">All - Payroll</option>
                          <option value="Payroll Processing">Payroll Processing</option>
                          <option value="Paid">Paid</option>
                        </optgroup>
                      </select>
                    </div>

                    {/* Bonus Recipient */}
                    <div>
                      <label
                        htmlFor="bonusRecipient"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Bonus Recipient
                      </label>
                      <input
                        type="text"
                        id="bonusRecipient"
                        value={filters.bonusRecipient}
                        onChange={(e) =>
                          handleFilterChange("bonusRecipient", e.target.value)
                        }
                        placeholder="Name or Email"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>

                    {/* Region */}
                    <div>
                      <label
                        htmlFor="region"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Region
                      </label>
                      <select
                        id="region"
                        value={filters.region}
                        onChange={(e) =>
                          handleFilterChange("region", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="All">All</option>
                        <option value="Texas North">Texas North</option>
                        <option value="Texas South">Texas South</option>
                        <option disabled>──────────</option>
                        <option value="Austin">Austin</option>
                        <option value="Houston">Houston</option>
                        <option value="DFW">DFW</option>
                        <option value="San Antonio">San Antonio</option>
                      </select>
                    </div>

                    {/* Page Size */}
                    <div>
                      <label
                        htmlFor="pageSize"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Results per Page
                      </label>
                      <select
                        id="pageSize"
                        value={filters.pageSize}
                        onChange={(e) => handleFilterChange("pageSize", parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={handleClearFilters}
                      className="px-6 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                    >
                      Clear
                    </button>
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isLoading ? "Searching..." : "Search"}
                    </button>
                  </div>
                </form>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <h2 className="text-lg font-semibold text-gray-900">
                      Results
                    </h2>
                    <div className="text-sm text-gray-500">
                      {totalCount > 0 ? (
                        <>
                          Showing{' '}
                          <span className="font-medium">
                            {(filters.page - 1) * filters.pageSize + 1}
                          </span>
                          {' '}to{' '}
                          <span className="font-medium">
                            {Math.min(filters.page * filters.pageSize, totalCount)}
                          </span>
                          {' '}of{' '}
                          <span className="font-medium">{totalCount}</span>
                          {' '}application{totalCount !== 1 ? 's' : ''}
                        </>
                      ) : (
                        '0 applications'
                      )}
                    </div>
                  </div>
                  {applications.length > 0 && (
                    <div className="relative group">
                      <button
                        onClick={handleExportToExcel}
                        disabled={isExporting}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        {isExporting ? "Exporting..." : "Export to Excel"}
                      </button>
                      {/* Tooltip */}
                      <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10">
                        Exports all records matching the current search filters (not just the current page)
                        <div className="absolute top-full right-4 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  )}
                </div>

                {isLoading ? (
                  <div className="flex justify-center py-12">
                    <CircularProgress />
                  </div>
                ) : applications.length === 0 ? (
                  <div className="text-gray-500 text-center p-8 bg-gray-50 rounded-lg">
                    <DocumentChartBarIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-lg font-medium">No applications found</p>
                    <p className="text-sm">
                      Try adjusting your search filters to find more results.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-4">
                      {applications.map((app: BonusApplication) => (
                        <ApplicationItem
                          key={app.bonusApplicationId}
                          app={app}
                          expandedRows={expandedRows}
                          toggleRowExpansion={toggleRowExpansion}
                          roleType={RoleType.Admin}
                          isReviewMode={false}
                        />
                      ))}
                    </div>

                    {totalPages > 1 && (
                      <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                        <div className="flex-1 flex justify-between sm:hidden">
                          <button
                            onClick={() => handlePageChange(filters.page - 1)}
                            disabled={filters.page === 1}
                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Previous
                          </button>
                          <button
                            onClick={() => handlePageChange(filters.page + 1)}
                            disabled={filters.page === totalPages}
                            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Next
                          </button>
                        </div>
                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                          <div>
                            <p className="text-sm text-gray-700">
                              Showing <span className="font-medium">{(filters.page - 1) * filters.pageSize + 1}</span> to{' '}
                              <span className="font-medium">
                                {Math.min(filters.page * filters.pageSize, totalCount)}
                              </span>{' '}
                              of <span className="font-medium">{totalCount}</span> results
                            </p>
                          </div>
                          <div>
                            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                              {/* First */}
                              <button
                                onClick={() => handlePageChange(1)}
                                disabled={filters.page === 1}
                                className="relative inline-flex items-center px-3 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                First
                              </button>

                              {/* Previous */}
                              <button
                                onClick={() => handlePageChange(filters.page - 1)}
                                disabled={filters.page === 1}
                                className="relative inline-flex items-center px-3 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Previous
                              </button>

                              {/* Page Numbers */}
                              {(() => {
                                const pages: (number | string)[] = [];
                                const delta = 2;

                                for (let i = 1; i <= totalPages; i++) {
                                  if (
                                    i === 1 ||
                                    i === totalPages ||
                                    (i >= filters.page - delta && i <= filters.page + delta)
                                  ) {
                                    pages.push(i);
                                  } else if (pages[pages.length - 1] !== '...') {
                                    pages.push('...');
                                  }
                                }

                                return pages.map((page, index) => {
                                  if (page === '...') {
                                    return (
                                      <span
                                        key={`ellipsis-${index}`}
                                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                                      >
                                        ...
                                      </span>
                                    );
                                  }

                                  return (
                                    <button
                                      key={page}
                                      onClick={() => handlePageChange(page as number)}
                                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${page === filters.page
                                        ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                        }`}
                                    >
                                      {page}
                                    </button>
                                  );
                                });
                              })()}

                              {/* Next */}
                              <button
                                onClick={() => handlePageChange(filters.page + 1)}
                                disabled={filters.page === totalPages}
                                className="relative inline-flex items-center px-3 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Next
                              </button>

                              {/* Last */}
                              <button
                                onClick={() => handlePageChange(totalPages)}
                                disabled={filters.page === totalPages}
                                className="relative inline-flex items-center px-3 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Last
                              </button>
                            </nav>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default StatusOverview;